// Text (decision B20, docs/CONTRACTS.md section 12): Norwegian bokmål by default, an English switch. Every visible
// string goes through t(); the choice is kept on this device only. The shell carries neither catalogue: each language
// is a chunk of its own (norsk.ts, english.ts), the stored one (Norwegian when none is) loads before the first render and
// the other only when it is chosen (docs/CONTRACTS.md section 21.7), and the screen keeps the language it shows until
// the new one has loaded.
// The eleven P4 areas (idea, marketing, solution, weights, desk, offline, enrich, jev, agents, alert, views) are in neither
// language chunk: a screen that shows one loads it through areaText() below, the one call for it (docs/CONTRACTS.md
// sections 22.6 (b) and 23.6), and scripts/measure.mjs counts it on that screen's route.
import { createContext, use, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Locale } from '@nw/shared/layers'
import { translate, type MessageKey } from '@nw/shared/messages/index'

export type { MessageKey }
export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string
type Catalogue = Readonly<Record<MessageKey, string>>

const KEY = 'nw:locale'
export const INTL_LOCALE: Record<Locale, string> = { nb: 'nb-NO', en: 'en-GB' }

function stored(): Locale | null {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'nb' || v === 'en' ? v : null
  } catch {
    return null
  }
}

const CHUNKS: Record<Locale, () => Promise<{ messages: Catalogue }>> = {
  nb: () => import('./norsk.ts'),
  en: () => import('./english.ts'),
}
const translators: Partial<Record<Locale, Translate>> = {}
const loading: Partial<Record<Locale, Promise<Translate>>> = {}
/** The first catalogue that arrived: both have the same keys (packages/shared/src/messages/messages.test.ts). */
let keys: Catalogue | undefined

/** A locale's translator, from its chunk, loaded once (a failed load may be tried again). */
function load(locale: Locale): Promise<Translate> {
  const ready = translators[locale]
  if (ready) return Promise.resolve(ready)
  return (loading[locale] ??= CHUNKS[locale]().then(
    ({ messages }) => {
      keys ??= messages
      return (translators[locale] = (key, vars) => translate(locale, messages, key, vars))
    },
    (e: unknown) => {
      delete loading[locale]
      throw e
    },
  ))
}

/** True when `key`, built at run time (`error.<code>`, a NACE division), is a message key. */
export function hasMessage(key: string): key is MessageKey {
  return keys !== undefined && key in keys
}

type Area = Readonly<Record<string, string>>
/** A P4 area set's loader per language: one area file's module, or several in an array (Promise.all). */
type AreaLoaders<M> = { nb: () => Promise<M>; en: () => Promise<unknown> }
type AreaKeys<M> = M extends readonly (infer E)[] ? AreaKeys<E> : { [A in keyof M]: keyof M[A] & string }[keyof M]
/** The translate function of a screen with P4 areas: the language chunk's keys and its areas' keys. */
export type AreaTranslate<K extends string> = (key: MessageKey | K, vars?: Record<string, string | number>) => string

/** Every area set a screen has registered: a language switch waits for each of them in the new language. */
const areaSets: { has(locale: Locale): boolean; load(locale: Locale): Promise<unknown> }[] = []
const areasReady = (locale: Locale) => Promise.all(areaSets.map((a) => a.load(locale)))

/**
 * The one call that loads P4 areas (docs/CONTRACTS.md sections 22.6 (b) and 23.6). Call it once at the top of the screen
 * module that shows them, with a dynamic import of each area file per language, written in that module so that
 * scripts/measure.mjs counts the areas on the screen's route; it returns the hook the screen's components call:
 *
 *   const useText = areaText({ nb: () => import('@nw/shared/messages/nb/idea'), en: () => import('@nw/shared/messages/en/idea') })
 *   // several areas: nb: () => Promise.all([import('…/nb/idea'), import('…/nb/views')]), and en the same
 *   export default function IdeasScreen() { const t = useText(); return t('idea.title') }
 *
 * The areas start loading in the language on screen when the module runs; the hook suspends (the route's pending
 * screen) until they are here, so the screen's first render has its text. A language switch waits for every area set
 * loaded so far, so the screen never shows the new language without its areas. A load that fails throws to the route's
 * error screen; the browser keeps the failed module for the page, so a page reload loads it again.
 */
export function areaText<M>(loaders: AreaLoaders<M>): () => AreaTranslate<AreaKeys<M>> {
  const text: Partial<Record<Locale, Area>> = {}
  const pending: Partial<Record<Locale, Promise<void>>> = {}
  const load = (locale: Locale): Promise<void> => {
    const p: Promise<void> = (pending[locale] ??= loaders[locale]().then(
      (m) => {
        text[locale] = Object.assign({}, ...[m].flat().flatMap((mod) => Object.values(mod as Record<string, Area>)))
        Object.assign(p, { status: 'fulfilled', value: undefined }) // React's thenable fields: use() returns at once
      },
      (e: unknown) => {
        delete pending[locale]
        throw e
      },
    ))
    return p
  }
  areaSets.push({ has: (locale) => !!text[locale], load })
  load(current).catch(() => {}) // the hook's use() reports a failure
  return function useAreaText() {
    const { locale, t } = useLocale()
    use(load(locale)) // always called, also once loaded: a use() that suspended must run again when React replays
    const area = text[locale]
    return useMemo<AreaTranslate<AreaKeys<M>>>(
      () => (key, vars) => (area && key in area ? translate(locale, area, key, vars) : t(key as MessageKey, vars)),
      [area, locale, t],
    )
  }
}

const keyOnly: Translate = (key) => key
const other = (locale: Locale): Locale => (locale === 'nb' ? 'en' : 'nb')

// The stored language (Norwegian when none is) starts loading before the first render; if it cannot load, the other.
const start: Locale = stored() ?? 'nb'
/** The language on screen (or about to be): an area starts loading in it as soon as its screen's chunk runs. */
let current: Locale = start
const first: Promise<Locale> = load(start).then(
  () => start,
  () => load(other(start)).then(() => other(start)),
)
first.catch(() => {}) // neither loaded: nothing can render, as when a chunk of the shell is missing

interface LocaleContext {
  locale: Locale
  /** True once the person picked a language on this device (then the account's locale no longer applies). */
  chosen: boolean
  setLocale(locale: Locale): void
  t: Translate
}

const Ctx = createContext<LocaleContext>({ locale: 'nb', chosen: false, setLocale: () => {}, t: keyOnly })

export function LocaleProvider({ children }: { children: ReactNode }) {
  // Nothing renders until the first catalogue has arrived (the stored language's, or if it fails the other's), so the
  // first screen is never shown in the other language.
  const [locale, setState] = useState<Locale | null>(() => (translators[start] ? start : null))
  const [chosen, setChosen] = useState(() => stored() !== null)
  const wanted = useRef<Locale>(start)

  /** Shows `next` at once if its catalogue is here, else when it arrives, unless another language was asked for since. */
  const show = useCallback((next: Locale): Promise<void> => {
    wanted.current = next
    if (translators[next] && areaSets.every((a) => a.has(next))) {
      setState(next)
      return Promise.resolve()
    }
    return Promise.all([load(next), areasReady(next)]).then(
      () => {
        if (wanted.current === next) setState(next)
      },
      () => {
        /* not loaded (offline, a replaced release): the screen stays in the language it shows */
      },
    )
  }, [])

  useEffect(() => {
    if (locale === null) void first.then(setState, () => {})
  }, [locale])

  useEffect(() => {
    if (locale) document.documentElement.lang = current = locale
  }, [locale])

  const setLocale = useCallback(
    (next: Locale) => {
      setChosen(true)
      try {
        localStorage.setItem(KEY, next)
      } catch {
        /* storage unavailable: the choice lasts for this page */
      }
      void show(next)
    },
    [show],
  )

  const value = useMemo<LocaleContext | null>(
    () => (locale ? { locale, chosen, setLocale, t: translators[locale] ?? keyOnly } : null),
    [locale, chosen, setLocale],
  )
  if (!value) return null
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** The translate function for the active language: const t = useT(); t('common.save'). */
export function useT(): Translate {
  return useContext(Ctx).t
}

export function useLocale(): LocaleContext {
  return useContext(Ctx)
}

/** Applies the account's locale from /api/me unless the person chose a language on this device. */
export function useAccountLocale(accountLocale: Locale | undefined): void {
  const { chosen, locale, setLocale } = useLocale()
  useEffect(() => {
    if (!chosen && accountLocale && accountLocale !== locale) setLocale(accountLocale)
  }, [accountLocale, chosen, locale, setLocale])
}
